import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { paymentsApi } from '../services/api';
import { CheckCircle, ArrowRight, Book, AlertCircle } from 'lucide-react';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  
  const courseId = searchParams.get('courseId');
  const reference = searchParams.get('id'); // WOMPI devuelve el ID como 'id'
  
  useEffect(() => {
    if (reference) {
      verifyPayment();
    } else {
      setStatus('error');
    }
  }, [reference]);

  const verifyPayment = async () => {
    try {
      console.log('🔍 Verificando pago con referencia:', reference);
      
      // Verificar el pago con WOMPI
      const response = await paymentsApi.verifyWompiPayment(reference!, courseId || undefined);
      
      console.log('✅ Verificación exitosa:', response.data);
      
      setPaymentInfo(response.data);
      setStatus('success');
      
    } catch (error) {
      console.error('❌ Error verificando pago:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Error en el Pago
          </h1>
          
          <p className="text-gray-600 mb-6">
            Hubo un problema verificando tu pago. Por favor contacta a soporte.
          </p>
          
          <Link 
            to="/dashboard"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Ir al Dashboard
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
        {/* Success Icon */}
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        
        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          ¡Pago Exitoso!
        </h1>
        
        <p className="text-gray-600 mb-6">
          Tu pago ha sido procesado correctamente y ya tienes acceso al curso.
        </p>
        
        {/* Course Info */}
        {paymentInfo?.course && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center mb-2">
              <Book className="w-5 h-5 text-blue-600 mr-2" />
              <span className="font-medium text-gray-900">Curso Adquirido</span>
            </div>
            <p className="text-sm text-gray-600">{paymentInfo.course.title}</p>
          </div>
        )}
        
        {/* Payment Details */}
        {reference && (
          <div className="text-xs text-gray-500 mb-6">
            <p>Referencia: {reference}</p>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="space-y-3">
          {courseId && (
            <Link 
              to={`/course/${courseId}`}
              className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Acceder al Curso
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          )}
          
          <Link 
            to="/courses"
            className="w-full inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Ver Mis Cursos
          </Link>
        </div>
        
        {/* Additional Info */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Recibirás un email de confirmación con los detalles de tu compra.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;